import clsx from 'clsx';
import { type FC } from 'react';

import { SECTIONS_IDS } from '../../../constants/sections';
import { ChevronForwardV4Icon } from '../../icons/ChevronForwardV4Icon';
import { ButtonLink } from '../common/ButtonLink';
import { Container } from '../common/Container';
import { Flex } from '../common/Flex';
import { Text } from '../common/Text';
import { IntegrationsHeroGlobeIcon } from '../IntegrationsHero/IntegrationsHeroIcons';

import styles from './IntegrationsMatchCtaBlock.module.scss';

const DocsExternalArrowIcon: FC = () => (
  <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M5.83333 2.83337H13.1667V10.1667" stroke="currentColor" strokeWidth={1.33} strokeLinecap="round" />
    <path
      d="M2.83337 13.1667V9.61115C2.83337 8.98648 3.06937 8.38737 3.48615 7.94337L13.1667 2.83337"
      stroke="currentColor"
      strokeWidth={1.33}
      strokeLinecap="round"
    />
    <path
      d="M9.83337 2.83337H13.1667V6.1667"
      stroke="currentColor"
      strokeWidth={1.33}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DEFAULT_MATCH_HEADLINE = "Didn't find the perfect match?";

const DEFAULT_MATCH_DESCRIPTION =
  "Our integration library is constantly expanding. Contact us, and we'll customize a solution tailored to your specific business needs.";

const DEFAULT_VISUAL_CAPTION = '100+ integrations worldwide';

const DEFAULT_ASIDE_CAPTION_ID = 'integrations-cta-globe-caption';

export type IntegrationsMatchCtaBlockProps = {
  headline?: string;
  description?: string;
  visualCaption?: string;
  asideCaptionId?: string;
};

export const IntegrationsMatchCtaBlock: FC<IntegrationsMatchCtaBlockProps> = ({
  headline = DEFAULT_MATCH_HEADLINE,
  description = DEFAULT_MATCH_DESCRIPTION,
  visualCaption = DEFAULT_VISUAL_CAPTION,
  asideCaptionId = DEFAULT_ASIDE_CAPTION_ID,
}) => {
  return (
    <Container className={styles.root}>
      <div className={styles.banner}>
        <div className={styles.copy}>
          <Text type="h2" className={styles.headline}>
            {headline}
          </Text>

          <Text type="regular16" color="white50" whiteSpace="normal" className={styles.description}>
            {description}
          </Text>

          <Flex className={styles.actions} gap={12} wrap="wrap" alignItems="center">
            <ButtonLink
              href={`#${SECTIONS_IDS.contact}`}
              variant="primary"
              iconRight={<ChevronForwardV4Icon />}
              className={clsx(styles.contactLinkPlain, styles.contactSalesButton)}
            >
              Contact Sales
            </ButtonLink>

            <ButtonLink
              href="/docs"
              isOutsideLink
              variant="primary"
              target="_blank"
              rel="noopener noreferrer"
              iconRight={<DocsExternalArrowIcon />}
              className={clsx(styles.contactLinkPlain, styles.docsLinkButton)}
            >
              API Documentation
            </ButtonLink>
          </Flex>
        </div>

        <aside className={styles.visual} aria-labelledby={asideCaptionId}>
          <div className={styles.globeStage}>
            <div className={styles.globeGlow} aria-hidden />

            <div className={styles.globeCircle}>
              <span className={styles.globeIcon}>
                <IntegrationsHeroGlobeIcon />
              </span>
            </div>
          </div>

          <Text id={asideCaptionId} type="regular14" align="center" whiteSpace="normal" className={styles.globeSubtext}>
            {visualCaption}
          </Text>
        </aside>
      </div>
    </Container>
  );
};
